/**
 * Main-thread handle to the fusion decode Worker (fusionDecodeWorker.ts).
 * Simpler than the old mosaic path's decoder: there's no canvas/toDataURL
 * round trip here — the raw band data goes straight into a WebGL texture
 * (fusionCompositor.ts), so this is just Worker request/response plumbing
 * plus turning the transferred bbox tuple into a Bounds3857 object.
 */
import type { StationDecodeRequest, StationDecodeResponse } from "./fusionDecodeProtocol";
import type { Bounds3857, OverviewLevel } from "./fusionGeo";

export interface StationDecodeResult {
  stationId: string;
  level: OverviewLevel;
  raw: Uint8Array;
  width: number;
  height: number;
  bbox: Bounds3857;
  bytesTransferred: number;
}

export class FusionDecoder {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, { resolve: (r: StationDecodeResult) => void; reject: (e: Error) => void }>();
  /** De-dupes identical in-flight requests — e.g. two viewport re-windows in
   * quick succession both wanting the same (station, ts, level). */
  private inFlight = new Map<string, Promise<StationDecodeResult>>();

  constructor() {
    this.worker = new Worker(new URL("./fusionDecodeWorker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (ev: MessageEvent<StationDecodeResponse>) => this.onMessage(ev.data);
  }

  decode(stationId: string, url: string, level: OverviewLevel, dedupeKey: string): Promise<StationDecodeResult> {
    const existing = this.inFlight.get(dedupeKey);
    if (existing) return existing;

    const id = this.nextId++;
    const promise = new Promise<StationDecodeResult>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
    }).finally(() => this.inFlight.delete(dedupeKey));

    this.inFlight.set(dedupeKey, promise);
    const req: StationDecodeRequest = { id, stationId, url, level };
    this.worker.postMessage(req);
    return promise;
  }

  private onMessage(msg: StationDecodeResponse): void {
    const waiter = this.pending.get(msg.id);
    if (!waiter) return;
    this.pending.delete(msg.id);
    if (!msg.ok) {
      waiter.reject(new Error(msg.error));
      return;
    }
    waiter.resolve({
      stationId: msg.stationId,
      level: msg.level,
      raw: new Uint8Array(msg.raw),
      width: msg.width,
      height: msg.height,
      bbox: { minX: msg.bbox[0], minY: msg.bbox[1], maxX: msg.bbox[2], maxY: msg.bbox[3] },
      bytesTransferred: msg.bytesTransferred,
    });
  }

  dispose(): void {
    this.worker.terminate();
    this.pending.clear();
    this.inFlight.clear();
  }
}
